import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Eye, Trash, MagnifyingGlass, PencilSimple, CopySimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QuotationRow({ q, onDelete, onDuplicate }) {
  return (
    <tr className="border-b border-stone-100 hover:bg-stone-50/50 transition-colors">
      <td className="py-3 px-4 font-semibold text-stone-900 whitespace-nowrap" data-testid={`quotation-number-${q.quotation_number}`}>{q.quotation_number}</td>
      <td className="py-3 px-4 text-stone-600 whitespace-nowrap">{q.date}</td>
      <td className="py-3 px-4 text-stone-700">{q.customer?.name || '—'}</td>
      <td className="py-3 px-4 text-stone-600">{q.company_name}</td>
      <td className="py-3 px-4 text-stone-600">{q.user_name}</td>
      <td className="py-3 px-4 text-stone-900 font-semibold text-right whitespace-nowrap">Rs. {q.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      <td className="py-3 px-4">
        <div className="flex items-center justify-center gap-0.5">
          <Link to={`/quotation/${q.id}`} data-testid={`view-quotation-${q.quotation_number}`}
            className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors" title="View">
            <Eye size={15} />
          </Link>
          <Link to={`/edit-quotation/${q.id}`} data-testid={`edit-quotation-${q.quotation_number}`}
            className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors" title="Edit">
            <PencilSimple size={15} />
          </Link>
          <button onClick={() => onDuplicate(q.id)} data-testid={`duplicate-quotation-${q.quotation_number}`}
            className="p-1.5 text-stone-400 hover:text-green-600 hover:bg-green-50 transition-colors" title="Duplicate">
            <CopySimple size={15} />
          </button>
          <button onClick={() => onDelete(q.id)} data-testid={`delete-quotation-${q.quotation_number}`}
            className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
            <Trash size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function QuotationsList() {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchQuotations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quotations`);
      setQuotations(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQuotations(); }, [fetchQuotations]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await axios.delete(`${API}/quotations/${id}`);
      fetchQuotations();
    } catch (e) { alert('Failed to delete'); }
  }, [fetchQuotations]);

  const handleDuplicate = useCallback(async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/duplicate`);
      fetchQuotations();
    } catch (e) { console.error(e); alert('Failed to duplicate'); }
  }, [fetchQuotations]);

  const filtered = quotations.filter(q =>
    q.quotation_number.toLowerCase().includes(search.toLowerCase()) ||
    q.company_name.toLowerCase().includes(search.toLowerCase()) ||
    (q.customer?.name || '').toLowerCase().includes(search.toLowerCase()) ||
    q.user_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl text-stone-900" data-testid="quotations-list-title">Quotations</h1>
          <p className="text-sm text-stone-500 mt-1">{quotations.length} total quotations</p>
        </div>
        <Link to="/create-quotation" data-testid="create-quotation-button"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#002480] transition-all active:scale-[0.97]">
          <Plus size={16} weight="bold" /> New Quotation
        </Link>
      </div>

      <div className="relative mb-6">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input type="text" data-testid="search-quotations" placeholder="Search by number, company, customer..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-96 bg-white border border-stone-200 pl-9 pr-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none transition-all" />
      </div>

      {loading ? (
        <div className="py-20 text-center text-stone-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-stone-200 py-16 text-center">
          <p className="text-stone-400 text-sm mb-3">No quotations found</p>
          <Link to="/create-quotation" className="text-[#002FA7] text-sm font-semibold hover:underline">Create your first quotation</Link>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="quotations-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Quote No.</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Customer</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">Company</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider">By</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider text-right">Total</th>
                <th className="py-3 px-4 font-semibold text-stone-600 text-xs uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <QuotationRow key={q.id} q={q} onDelete={handleDelete} onDuplicate={handleDuplicate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
